import { AlertCircleIcon } from 'lucide-react-native';
import { Controller, type Control, type FieldValues } from 'react-hook-form';

import { getSurveyAccessoryViewId, SurveyKeyboardDoneBar } from './SurveyKeyboardDoneBar';
import { SurveyQuestionLabel } from './SurveyQuestionLabel';
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { Input, InputField } from '@/components/ui/input';
import { getSurveyKeyboardProps, needsKeyboardDismissAffordance } from './surveyKeyboard';
import type { SurveyElement } from '../../model/surveySchema';

interface SurveyTextQuestionProps {
  element: SurveyElement;
  control: Control<FieldValues>;
  errorMessage?: string;
  hideRequiredMarker?: boolean;
}

export function SurveyTextQuestion({
  element,
  control,
  errorMessage,
  hideRequiredMarker,
}: SurveyTextQuestionProps) {
  const keyboardProps = getSurveyKeyboardProps(element);

  const needsDoneBar = needsKeyboardDismissAffordance(element);

  return (
    <Controller
      name={element.name}
      control={control}
      render={({ field: { onChange, onBlur, value } }) => (
        <FormControl
          isInvalid={!!errorMessage}
          aria-required={element.isRequired}
          className="gap-2">
          <SurveyQuestionLabel element={element} hideRequiredMarker={hideRequiredMarker} />

          <Input>
            <InputField
              testID={element.name}
              {...keyboardProps}
              value={typeof value === 'string' ? value : ''}
              onChangeText={onChange}
              onBlur={onBlur}
              inputAccessoryViewID={
                needsDoneBar ? getSurveyAccessoryViewId(element.name) : undefined
              }
              accessibilityLabel={element.isRequired ? `${element.title}, required` : element.title}
            />
          </Input>

          {needsDoneBar ? <SurveyKeyboardDoneBar fieldName={element.name} /> : null}

          <FormControlError>
            <FormControlErrorIcon as={AlertCircleIcon} />
            <FormControlErrorText>{errorMessage}</FormControlErrorText>
          </FormControlError>
        </FormControl>
      )}
    />
  );
}
